// packages/core/src/learning/school-modules/life-design.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Life design". surface: 'school', homeSchool: 'life-design'.
// module id 'school-life-design' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #14b8a6) — flag for the presentation-tuning pass.
// tier/pillar: life-design lens → tier 'life-design', pillar 'life-decisions'
// (NOTE: 'life-design' is also a valid core-path Tier value; the school id and
// the tier id collide by name but are distinct concepts — the tier is the
// curriculum grouping, the homeSchool is the school id).
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_LIFE_DESIGN_MODULE: LearningModule = {
	id: 'school-life-design',
	title: 'Life design',
	description:
		'The lens that turns money into a tool for building better days — testing location, work, housing, and pace against real numbers instead of inherited defaults.',
	emoji: '🧭', // PLACEHOLDER — tune in presentation pass
	accentColor: '#14b8a6', // from SchoolMeta color — tune in presentation pass
	tier: 'life-design',
	pillar: 'life-decisions',
	order: 0,
	surface: 'school',
	homeSchool: 'life-design',
	lessons: [
		{
			id: 'school-life-design-01',
			title: 'Money should shape better days',
			description:
				'This lesson teaches the central life design idea: money should support the life you actually want to live.',
			pillar: 'life-decisions',
			schoolsOfThought: ['life-design'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['daily-life-design', 'values-based-money', 'ordinary-week'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'daily life design',
				'values based money',
				'ordinary week',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Life design starts with a simple but uncomfortable question: what is your money building in your actual life?',
				},
				{
					type: 'paragraph',
					text: 'This lens believes the point of money is not only a bigger number. The point is better days, better choices, better energy, better relationships, and a life that fits the person you are becoming.',
				},
				{
					type: 'bullet_list',
					items: [
						'Your calendar matters',
						'Your energy matters',
						'Your location matters',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is chasing financial goals that look responsible but do not improve the life you live.',
				},
				{
					type: 'example',
					text: 'Someone may earn more money but spend every week exhausted, commuting, overworked, and disconnected from people they care about. The account looks better, but the life may not.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that life design can become vague if it avoids numbers. A dream life still has rent, taxes, travel costs, health needs, family obligations, and tradeoffs.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to Dashboard and planning. You are not asking for fantasy. You are asking whether your current money picture supports the ordinary week you want.',
				},
				{
					type: 'key_takeaway',
					text: 'Life design is strongest when your money supports days you would actually choose.',
				},
			],
			audioScript:
				'Life design starts with a question most money content skips: what kind of life is this money supposed to support?\n\nNot in a vague motivational way. Not a perfect beach photo. Not some fantasy where every hard part disappears. The question is more practical than that. What do you want a good ordinary week to feel like? Where do you wake up? How much control do you have over your time? Who do you see? How much stress follows you around? What kind of work drains you, and what kind of work gives you energy? What do you want more room for?\n\nLife design believes money is only useful if it connects to real life. A bigger account balance can be good, but it does not automatically create a better day. A higher salary can help, but if it comes with a schedule that crushes your health, a commute that eats your life, or pressure that makes you numb, the tradeoff needs to be named.\n\nThis is what the life design lens notices. Money decisions are also calendar decisions. Housing decisions are energy decisions. Career decisions are relationship decisions. Location decisions are cost decisions. Spending decisions are identity decisions. You are not only managing dollars. You are building a way of living.\n\nA person can look successful from the outside and still feel trapped inside the day to day reality. Nice job title, decent income, good apartment, normal lifestyle, and still no time, no calm, no room to think. Life design does not automatically say to quit or move or start over. It asks a better question: is this setup producing the life you actually want?\n\nBut this lens has a blind spot. It can turn into fantasy if it avoids math. Dreaming about living abroad, working less, moving cities, changing careers, or building a calmer life is not the same as making the numbers work. Rent matters. Income matters. Debt matters. Family matters. Health matters. Stability matters. A life design idea has to survive contact with real constraints.\n\nThat is why the healthy version is both imaginative and grounded. You name the life. Then you test the tradeoff. What would it cost? What would it require? What would you give up? What would you gain? What would need to be true before it became responsible?\n\nInside Nisk, this starts with your Dashboard. Not because your current numbers define your future, but because they show the starting point. Then planning turns the idea into something you can test. You are not trying to copy someone else’s dream. You are trying to understand whether your money is helping you build better days.\n\nLife design is not about escaping reality. It is about refusing to let reality be shaped only by defaults.',
			flashcards: [
				{
					id: 'school-life-design-01-fc-1',
					role: 'main_idea',
					front: 'What does life design ask?',
					back: 'What kind of life your money is actually building.',
				},
				{
					id: 'school-life-design-01-fc-2',
					role: 'watch_out',
					front: 'What is the fantasy trap?',
					back: 'Dreaming about a better life without testing real numbers.',
				},
				{
					id: 'school-life-design-01-fc-3',
					role: 'use_it',
					front: 'What should you define?',
					back: 'Define the ordinary week your money should support.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your current setup',
			},
			revision: 1,
		},
		{
			id: 'school-life-design-02',
			title: 'Your calendar shows your values',
			description:
				'This lesson teaches how life design reads time, energy, and attention as money outcomes.',
			pillar: 'life-decisions',
			schoolsOfThought: ['life-design'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['calendar-truth', 'time-allocation', 'attention-cost'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['calendar truth', 'time allocation', 'attention cost'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Your values are not only what you say you care about. They show up in your calendar, your bills, your commute, your stress, and your attention.',
				},
				{
					type: 'paragraph',
					text: 'Life design believes money choices should be judged by what they do to your actual time. A choice that looks good financially may still be expensive if it steals every evening, weekend, or quiet hour.',
				},
				{
					type: 'bullet_list',
					items: [
						'Time is a cost',
						'Attention is a cost',
						'Stress is a cost',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is counting dollars while ignoring the life hours attached to them.',
				},
				{
					type: 'example',
					text: 'A cheaper apartment with a brutal commute may save rent but cost sleep, gym time, family time, and focus. The dollar savings are real, but they are not the whole story.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that not every person can choose a perfect calendar. Some seasons require sacrifice. Some jobs, family duties, and costs are not easy to change quickly.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens becomes a reflection and planning habit. You are asking which parts of your calendar are chosen, which are inherited, and which money moves could change them.',
				},
				{
					type: 'key_takeaway',
					text: 'Life design treats time, energy, and attention as real costs.',
				},
			],
			audioScript:
				'Life design pays close attention to your calendar because your calendar tells the truth. Not the whole truth, but a lot of it.\n\nMost people say they value health, relationships, rest, creativity, freedom, family, learning, or peace. But then the calendar shows something else. Long commute. Exhausting job. Too many obligations. No time to cook. No time to train. No time to think. No time with people who matter. No quiet space. No recovery. No margin.\n\nThat is not always because someone is lazy or careless. Often it is because the money setup is shaping the time setup. Rent choice shapes commute. Job choice shapes energy. Car payments shape work pressure. Subscriptions and lifestyle costs shape how much income is required. Debt shapes stress. Lack of runway shapes how much you can say no.\n\nLife design looks at this and says: money decisions are time decisions.\n\nA cheaper apartment may save money, but if it adds ninety minutes of commuting every day, the savings are not the whole story. A higher paying job may be worth it, but if it destroys your sleep and relationships, the income is not the only number that matters. A bigger lifestyle may feel good, but if it forces you to keep work you hate, the hidden cost is control.\n\nThis lens does not say every uncomfortable tradeoff is wrong. Sometimes a hard season is part of the plan. Maybe you take a demanding role to build career capital. Maybe you live farther away for a while to save money. Maybe you work extra because you are cleaning up debt or building runway. Life design is not allergic to sacrifice.\n\nThe question is whether the sacrifice is chosen and whether it is still serving the life you want.\n\nThat is the difference between intentional tradeoff and accidental drift. Intentional tradeoff says, I am choosing this for a season because it supports a bigger plan. Accidental drift says, I never really chose this, but now my bills, habits, and schedule own me.\n\nInside Nisk, this lesson may not need a complicated action. It starts with looking at the week. Where does your time go? What part of your schedule is bought by your spending? What part is controlled by your debt? What part is controlled by rent, location, transportation, or work? What money change would give you back the most energy?\n\nA calendar audit can be uncomfortable because it removes excuses. But it is also freeing. Once you see how money shapes your time, you can start testing different choices.\n\nLife design says your money plan should not only make the numbers better. It should make your lived week more honest.',
			flashcards: [
				{
					id: 'school-life-design-02-fc-1',
					role: 'main_idea',
					front: 'Why does calendar matter?',
					back: 'It shows how money choices shape time, energy, and attention.',
				},
				{
					id: 'school-life-design-02-fc-2',
					role: 'watch_out',
					front: 'What do people ignore?',
					back: 'The life hours attached to financial choices.',
				},
				{
					id: 'school-life-design-02-fc-3',
					role: 'use_it',
					front: 'What should you audit?',
					back: 'Audit which parts of your week are chosen or inherited.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Audit your calendar',
				prompt: 'Write which parts of your week feel chosen, which feel inherited, and which money choices keep them in place.',
			},
			revision: 1,
		},
		{
			id: 'school-life-design-03',
			title: 'Design beats default',
			description:
				'This lesson teaches how life design challenges default paths around work, housing, location, and lifestyle.',
			pillar: 'life-decisions',
			schoolsOfThought: ['life-design'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'default-path',
				'intentional-tradeoffs',
				'life-architecture',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'default path',
				'intentional tradeoffs',
				'life architecture',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A default life can look normal and still feel wrong. Graduate, rent, commute, upgrade, borrow, work more, spend more, repeat. Life design asks whether that path actually fits you.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes many money decisions are inherited from culture before they are chosen. The normal path is not always bad, but it should be inspected.',
				},
				{
					type: 'bullet_list',
					items: [
						'Normal is not the same as fitting',
						'Prestige is not the same as freedom',
						'More is not always better',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is letting social expectations pick your costs before you pick your life.',
				},
				{
					type: 'example',
					text: 'Someone may buy a nicer car because it feels like the next adult move. The payment may be affordable, but it can still reduce runway, flexibility, and career options.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that designing your life can mean disappointing people, moving slower, or choosing a path that does not look impressive from the outside.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens connects to Simulate. You can test how different life setups change the pressure, runway, and options behind the choice.',
				},
				{
					type: 'key_takeaway',
					text: 'Life design means choosing tradeoffs on purpose instead of inheriting them by default.',
				},
			],
			audioScript:
				'A default life can look perfectly normal from the outside and still feel wrong from the inside.\n\nThat is why life design is such a useful lens. It does not assume normal is bad. It just refuses to treat normal as automatic wisdom. The normal path may work for you. A stable job, a home, a car, a family, a familiar city, a steady routine, all of that can be good. But only if it actually fits. Life design asks you to inspect the path before you spend years funding it.\n\nA lot of money choices are inherited before they are chosen. You choose the apartment that matches what people at your income level usually rent. You choose the car that feels like the next adult step. You choose the job that sounds impressive. You choose the city that feels expected. You upgrade when income rises because that is what the people around you do. None of these choices are automatically wrong. But together, they can build a life with high fixed costs and low flexibility.\n\nThat is the default trap. You did not exactly choose the cage. You just kept accepting pieces of it.\n\nLife design believes your life has architecture. Work is one wall. Housing is another. Transportation is another. Relationships, health, location, spending habits, and debt all shape the structure. If the structure is poorly designed, you feel it every day. Not as one obvious disaster, but as pressure. You feel it as no time, no energy, no room to move, no ability to say no.\n\nDesign beats default because design makes the tradeoff visible. Maybe you choose the smaller apartment because freedom matters more than space right now. Maybe you keep the older car because flexibility matters more than image. Maybe you move closer to work because time matters more than square footage. Maybe you choose a higher cost city because relationships and opportunity make it worth it. The point is not that one choice is always right. The point is that the choice is conscious.\n\nThere is a cost to this. Choosing intentionally can disappoint people. It can look weird. It can feel slower. You may choose something less impressive because it fits your life better. That takes nerve, especially when everyone around you is playing the normal status game.\n\nInside Nisk, this belongs in simulation. You can test a life setup instead of only imagining it. What happens if fixed costs drop? What happens if you move? What happens if a car payment disappears? What happens if you choose more runway over more status? The projection is not a command. It is a way to see the tradeoff.\n\nLife design does not tell you to reject normal. It tells you to stop obeying normal without checking the cost.',
			flashcards: [
				{
					id: 'school-life-design-03-fc-1',
					role: 'main_idea',
					front: 'What does design beat?',
					back: 'Default choices that build costs before you choose a life.',
				},
				{
					id: 'school-life-design-03-fc-2',
					role: 'watch_out',
					front: 'What is the default trap?',
					back: 'Letting social expectations pick your costs and lifestyle.',
				},
				{
					id: 'school-life-design-03-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how different life setups change pressure and options.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a different setup',
				scenarioType: 'custom',
			},
			revision: 1,
		},
		{
			id: 'school-life-design-04',
			title: 'Freedom needs a real plan',
			description:
				'This lesson teaches why life design must be tested against money, responsibilities, risk, and timing.',
			pillar: 'life-decisions',
			schoolsOfThought: ['life-design'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['grounded-freedom', 'constraint-testing', 'plan-realism'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'grounded freedom',
				'constraint testing',
				'plan realism',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Life design can sound dreamy fast. Move somewhere cheaper. Work less. Travel more. Start over. Build a slower life. The ideas may be valid, but they need math.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes freedom should be designed, not wished for. A better life needs numbers, timing, runway, income, obligations, and risk checks.',
				},
				{
					type: 'bullet_list',
					items: [
						'Count the cost',
						'Name the risk',
						'Build the runway',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing inspiration with readiness.',
				},
				{
					type: 'example',
					text: 'Moving to a cheaper city may lower rent, but the full decision includes income, transportation, healthcare, relationships, job options, safety, and return plans.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that planning can make dreams feel less exciting. That is not a bad thing. A dream that survives honest math becomes stronger.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in freedom planning and runway checks. You are turning a life idea into a testable plan.',
				},
				{
					type: 'key_takeaway',
					text: 'Life design becomes powerful when the dream survives real numbers.',
				},
			],
			audioScript:
				'Life design can get dreamy very quickly. That is part of its appeal. You start imagining a different city, a calmer job, more travel, more creative work, more time with family, more control over your mornings, or a lifestyle that does not feel like constant pressure. Those ideas can be meaningful. They can also become escape fantasies if they never touch real numbers.\n\nThat is why this lens needs a plan.\n\nA life design idea is not ready just because it feels exciting. Moving somewhere cheaper might be smart, but what happens to income? What happens to transportation? What happens to healthcare? What happens to relationships? What happens if the job does not work out? What is the return plan? How much runway would make the move less fragile?\n\nWorking less might sound amazing, but what spending would need to change? What benefits would change? What debt still needs attention? What obligations are fixed? What risks are acceptable? What risks are not?\n\nStarting a business, changing careers, going remote, living abroad, moving out, moving back home, taking a sabbatical, choosing a smaller life, these can all be valid life design moves. But they are not automatically wise because they sound freeing. Freedom without math can become stress with better branding.\n\nThe life design lens is strongest when it respects constraints. It does not pretend you can manifest your way around rent. It does not ignore family responsibilities. It does not shame someone for needing stability. It does not act like everyone can redesign everything at once. Instead, it asks: what can be tested? What can be staged? What needs to be true first?\n\nMaybe the first step is not quitting. Maybe it is building three months of runway. Maybe it is lowering one fixed cost. Maybe it is testing remote work for a short period. Maybe it is pricing the real monthly cost of a different city. Maybe it is building skills before making the leap. Maybe it is waiting, not because you are scared, but because timing matters.\n\nPlanning can make a dream feel less romantic. That is okay. If an idea falls apart when you add numbers, it was not ready. If it survives the numbers, it becomes stronger. Now it is not just a mood. It is a plan.\n\nInside Nisk, this means using Dashboard, runway, and freedom planning. You are not asking the app to tell you what to do. You are using it to make the tradeoffs visible. How much would this life cost? How long would it take? What cushion would make it safer? What would you give up? What would you gain?\n\nLife design becomes powerful when imagination and math finally talk to each other.',
			flashcards: [
				{
					id: 'school-life-design-04-fc-1',
					role: 'main_idea',
					front: 'What does freedom need?',
					back: 'Real numbers, timing, runway, obligations, and risk checks.',
				},
				{
					id: 'school-life-design-04-fc-2',
					role: 'watch_out',
					front: 'What is false readiness?',
					back: 'Feeling inspired before the plan survives the math.',
				},
				{
					id: 'school-life-design-04-fc-3',
					role: 'use_it',
					front: 'How do you test a dream?',
					back: 'Count the cost, name the risk, and build the runway.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
			revision: 1,
		},
		{
			id: 'school-life-design-05',
			title: 'Where life design breaks down',
			description:
				'This lesson teaches the limits of life design and how to avoid turning intentional living into avoidance, privilege blindness, or constant reinvention.',
			pillar: 'life-decisions',
			schoolsOfThought: ['life-design'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'life-design-limits',
				'avoidance-risk',
				'chosen-constraints',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'life design limits',
				'avoidance risk',
				'chosen constraints',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Life design is powerful, but it can become slippery. If every hard season feels like proof that your life is wrong, you may keep redesigning instead of building.',
				},
				{
					type: 'paragraph',
					text: 'This lens breaks down when it treats discomfort as failure. Some meaningful lives include stress, duty, repetition, care work, sacrifice, and seasons where freedom is limited.',
				},
				{
					type: 'bullet_list',
					items: [
						'Not every constraint is bad',
						'Not every hard season means escape',
						'Not every dream deserves a rebuild',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using life design to avoid commitment, discipline, or responsibility.',
				},
				{
					type: 'example',
					text: 'Someone may want to move every time work gets hard. The real issue may not be the city. It may be boundaries, skill growth, debt pressure, or choosing better work over time.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is privilege. Some people have more room to redesign than others because of income, family support, health, citizenship, location, or safety.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, the mature move is to test the idea and name the constraint. You are not trying to escape every hard thing. You are trying to choose the right hard things.',
				},
				{
					type: 'key_takeaway',
					text: 'Life design works when it helps you choose your constraints, not when it helps you run from all of them.',
				},
			],
			audioScript:
				'Life design is useful because it gives people permission to question the default path. But like every strong lens, it can go too far.\n\nThe biggest risk is that life design can become avoidance. Every time work gets hard, you imagine a new career. Every time a city feels boring, you imagine moving. Every time discipline is required, you call it misalignment. Every time a relationship, job, goal, or responsibility asks for patience, you wonder if you need a full life reset.\n\nSometimes the answer really is change. A bad job can damage your health. A city can be the wrong fit. A lifestyle can be too expensive. A relationship can be unhealthy. A schedule can be unsustainable. Life design is right to question these things.\n\nBut not every hard thing is a sign that your life is wrong.\n\nSome hard things are chosen constraints. Building a skill is hard. Caring for family can be hard. Staying consistent with health can be hard. Paying off debt can be hard. Saving for a move can be hard. Starting over can be hard. Building a business can be hard. A meaningful life is not a life with no friction. It is a life where the friction is connected to something you actually value.\n\nThat is where the mature life design lens becomes sharper. It stops asking, how do I escape discomfort? It starts asking, which discomfort is worth choosing?\n\nThere is also a privilege blind spot. Some people can redesign faster because they have savings, family support, remote work, health, a strong passport, no dependents, or a high income. Other people have less room. They may be supporting relatives, dealing with medical needs, managing debt, living somewhere expensive, or working jobs with less flexibility. Life design should never pretend everyone has the same set of options.\n\nThat does not make the lens useless. It makes it more serious. For some people, life design means a huge move. For others, it means one night a week protected for study. It means building runway slowly. It means choosing a cheaper apartment. It means setting a boundary. It means staying in a hard season because it serves a real plan.\n\nInside Nisk, this lesson becomes a test of honesty. Is the life idea a real design move, or is it escape? What constraint are you trying to remove? What constraint are you willing to choose? What numbers would make the change safer? What responsibility still needs respect?\n\nLife design is not about running from every hard thing. It is about choosing the hard things that build the life you actually want.',
			flashcards: [
				{
					id: 'school-life-design-05-fc-1',
					role: 'main_idea',
					front: 'Where can life design fail?',
					back: 'It can become avoidance, privilege blindness, or constant reinvention.',
				},
				{
					id: 'school-life-design-05-fc-2',
					role: 'watch_out',
					front: 'What is the escape trap?',
					back: 'Treating every hard season as proof your life is wrong.',
				},
				{
					id: 'school-life-design-05-fc-3',
					role: 'use_it',
					front: 'What should you choose?',
					back: 'Choose the constraints that support the life you actually value.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name your chosen constraint',
				prompt: 'Write one hard thing you are willing to choose because it supports the life you want.',
			},
			revision: 1,
		},
	],
};
